import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Stack,
    Typography,
    Alert,
    Skeleton,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { colors, InfoCard, Button } from '@components/design-system';
import { useBannersBulkMutation, useBannersQuery } from '@features/redux/apis/banners';
import {
    addAddition,
    BANNER_REQUIREMENTS_SUMMARY,
    buildBulkRequest,
    buildInitialDraft,
    getVisibleOrder,
    hasInvalidAdditions,
    hasPendingChanges,
    moveItem,
    removeAddition,
    togglePendingDeletion,
    validateAndReadBannerFile,
} from './bannerEditor.utils';

import type { BannerListItem } from '@api/banners';
import type { BannerEditorDraft, BannerEditorProps } from './BannerEditor.types';

const PREVIEW_HEIGHT = 96;

// Module-level constant so the default value never produces a new array ref
// (otherwise the draft-sync effect would loop while the query is pending).
const EMPTY_BANNERS: BannerListItem[] = [];

const generateTempId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const BannerEditor: React.FC<BannerEditorProps> = ({
    scope,
    companyId,
    canEdit,
    title = 'Banners',
    description,
    wrapped = true,
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [draft, setDraft] = useState<BannerEditorDraft>({ existing: [], additions: [] });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data, isLoading, isFetching } = useBannersQuery({ scope, companyId });
    const banners = data ?? EMPTY_BANNERS;
    const [publishBanners, { isLoading: publishing }] = useBannersBulkMutation();

    useEffect(() => {
        setDraft(buildInitialDraft(banners));
    }, [banners]);

    const visible = useMemo(() => getVisibleOrder(draft), [draft]);
    const pending = hasPendingChanges(draft);
    const blockedByInvalid = hasInvalidAdditions(draft);

    const handleFiles = async (files: FileList | null) => {
        if (!files || !files.length) return;
        setErrorMessage(null);

        const results = await Promise.all(
            Array.from(files).map(async (file) => ({ file, result: await validateAndReadBannerFile(file) }))
        );

        const firstHardFail = results.find(({ result }) => result.kind === 'hardFail');
        if (firstHardFail && firstHardFail.result.kind === 'hardFail') {
            setErrorMessage(`${firstHardFail.file.name}: ${firstHardFail.result.reason}`);
        }

        const additions = results
            .filter(({ result }) => result.kind !== 'hardFail')
            .map(({ result }) => {
                if (result.kind === 'hardFail') throw new Error('unreachable');
                return {
                    tempId: generateTempId(),
                    dataUrl: result.dataUrl,
                    mimeType: result.mimeType,
                    invalidReason: result.kind === 'invalid' ? result.reason : undefined,
                };
            });

        if (!additions.length) return;

        setDraft((current) =>
            additions.reduce((accumulated, addition) => addAddition(accumulated, addition), current)
        );
    };

    const handleAddClick = () => {
        fileInputRef.current?.click();
    };

    const handleDiscard = () => {
        setDraft(buildInitialDraft(banners));
        setErrorMessage(null);
    };

    const handlePublish = async () => {
        try {
            setErrorMessage(null);
            const payload = buildBulkRequest(draft, scope, companyId);
            await publishBanners({ payload }).unwrap();
        } catch (error) {
            setErrorMessage(
                error instanceof Error ? error.message : 'Failed to publish banner changes'
            );
        }
    };

    const renderCard = (orderedEntry: ReturnType<typeof getVisibleOrder>[number], index: number) => {
        const isFirst = index === 0;
        const isLast = index === visible.length - 1;
        const positionLabel = `Banner ${index + 1}`;

        if (orderedEntry.kind === 'existing') {
            const { item, pendingDeletion } = orderedEntry.entry;
            return (
                <BannerCard
                    key={`existing-${item.BannerID}`}
                    src={item.SignedUrl}
                    label={positionLabel}
                    badge={pendingDeletion ? 'Will be removed' : null}
                    badgeColor={colors.warning.dark}
                    badgeBg={colors.warning.light}
                    canEdit={canEdit}
                    isMobile={isMobile}
                    onMoveUp={() =>
                        setDraft((current) =>
                            moveItem(current, { kind: 'existing', bannerId: item.BannerID }, -1)
                        )
                    }
                    onMoveDown={() =>
                        setDraft((current) =>
                            moveItem(current, { kind: 'existing', bannerId: item.BannerID }, 1)
                        )
                    }
                    onAction={() =>
                        setDraft((current) => togglePendingDeletion(current, item.BannerID))
                    }
                    actionIcon={pendingDeletion ? <RestoreOutlinedIcon /> : <DeleteOutlineIcon />}
                    actionLabel={pendingDeletion ? 'Undo remove' : 'Remove'}
                    isFirst={isFirst}
                    isLast={isLast}
                />
            );
        }

        const addition = orderedEntry.entry;
        const isInvalid = Boolean(addition.invalidReason);
        return (
            <BannerCard
                key={`addition-${addition.tempId}`}
                src={addition.dataUrl}
                label={positionLabel}
                badge={isInvalid ? 'Invalid' : 'New'}
                badgeColor={isInvalid ? colors.error.dark : colors.success.dark}
                badgeBg={isInvalid ? colors.error.light : colors.success.light}
                subLabel={addition.invalidReason}
                outlineColor={isInvalid ? colors.error.main : undefined}
                canEdit={canEdit}
                isMobile={isMobile}
                disableReorder={isInvalid}
                onMoveUp={() =>
                    setDraft((current) =>
                        moveItem(current, { kind: 'addition', tempId: addition.tempId }, -1)
                    )
                }
                onMoveDown={() =>
                    setDraft((current) =>
                        moveItem(current, { kind: 'addition', tempId: addition.tempId }, 1)
                    )
                }
                onAction={() => setDraft((current) => removeAddition(current, addition.tempId))}
                actionIcon={<DeleteOutlineIcon />}
                actionLabel='Remove'
                isFirst={isFirst}
                isLast={isLast}
            />
        );
    };

    const showInitialLoader = isLoading && !data;
    const showRefetchIndicator = isFetching && !showInitialLoader;

    const body = (
        <Stack spacing={2}>
            {description && (
                <Typography variant='body2' sx={{ color: colors.neutral[700] }}>
                    {description}
                </Typography>
            )}

            {canEdit && (
                <Typography variant='caption' sx={{ color: colors.neutral[500] }}>
                    Image requirements: {BANNER_REQUIREMENTS_SUMMARY}
                </Typography>
            )}

            {errorMessage && (
                <Alert severity='error' onClose={() => setErrorMessage(null)}>
                    {errorMessage}
                </Alert>
            )}

            {showInitialLoader ? (
                <Stack spacing={1.5}>
                    <BannerCardSkeleton isMobile={isMobile} />
                    <BannerCardSkeleton isMobile={isMobile} />
                </Stack>
            ) : (
                <>
                    {visible.length === 0 && (
                        <Typography variant='body2' sx={{ color: colors.neutral[500] }}>
                            No banners configured yet.
                        </Typography>
                    )}

                    <Stack
                        spacing={1.5}
                        sx={{ opacity: showRefetchIndicator ? 0.6 : 1, transition: 'opacity 120ms ease' }}>
                        {visible.map((entry, index) => renderCard(entry, index))}
                    </Stack>
                </>
            )}

            {canEdit && (
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={1.5}
                    alignItems={isMobile ? 'stretch' : 'center'}>
                    <input
                        ref={fileInputRef}
                        type='file'
                        multiple
                        accept='image/png,image/jpeg,image/gif,image/webp'
                        style={{ display: 'none' }}
                        onChange={(event) => {
                            handleFiles(event.target.files);
                            event.target.value = '';
                        }}
                    />
                    <Button
                        variant='secondary'
                        startIcon={<AddPhotoAlternateOutlinedIcon />}
                        onClick={handleAddClick}
                        disabled={publishing || showInitialLoader}>
                        Add image
                    </Button>
                    <Button
                        variant='secondary'
                        onClick={handleDiscard}
                        disabled={!pending || publishing || showInitialLoader}>
                        Discard changes
                    </Button>
                    {!isMobile && <Box sx={{ flex: 1 }} />}
                    <Button
                        variant='primary'
                        onClick={handlePublish}
                        disabled={!pending || publishing || blockedByInvalid || showInitialLoader}>
                        {publishing ? 'Publishing…' : 'Publish'}
                    </Button>
                </Stack>
            )}
        </Stack>
    );

    if (!wrapped) return body;

    return (
        <InfoCard title={title} icon={<ImageOutlinedIcon />}>
            {body}
        </InfoCard>
    );
};

type BannerCardProps = {
    src: string;
    label: string;
    badge: string | null;
    badgeColor: string;
    badgeBg: string;
    subLabel?: string;
    outlineColor?: string;
    canEdit: boolean;
    isMobile?: boolean;
    disableReorder?: boolean;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onAction: () => void;
    actionIcon: React.ReactNode;
    actionLabel: string;
    isFirst: boolean;
    isLast: boolean;
};

const BannerCard: React.FC<BannerCardProps> = ({
    src,
    label,
    badge,
    badgeColor,
    badgeBg,
    subLabel,
    outlineColor,
    canEdit,
    isMobile = false,
    disableReorder = false,
    onMoveUp,
    onMoveDown,
    onAction,
    actionIcon,
    actionLabel,
    isFirst,
    isLast,
}) => {
    const previewBox = (
        <Box
            sx={{
                width: isMobile ? '100%' : 168,
                height: isMobile ? 140 : PREVIEW_HEIGHT,
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: colors.neutral[100],
            }}>
            <img
                src={src}
                alt={label}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
        </Box>
    );

    const meta = (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction='row' spacing={1} alignItems='center'>
                <Typography variant='body2' sx={{ fontWeight: 600, color: colors.neutral[800] }}>
                    {label}
                </Typography>
                {badge && (
                    <Box
                        sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: badgeBg,
                            color: badgeColor,
                        }}>
                        {badge}
                    </Box>
                )}
            </Stack>
            {subLabel && (
                <Typography
                    variant='caption'
                    sx={{ color: colors.error.dark, display: 'block', mt: 0.5 }}>
                    {subLabel}
                </Typography>
            )}
        </Box>
    );

    const actions = canEdit && (
        <Stack
            direction='row'
            spacing={0.5}
            sx={{ flexWrap: 'wrap', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
            <Button
                variant='secondary'
                size='small'
                onClick={onMoveUp}
                disabled={isFirst || disableReorder}
                startIcon={<ArrowUpwardIcon />}>
                Up
            </Button>
            <Button
                variant='secondary'
                size='small'
                onClick={onMoveDown}
                disabled={isLast || disableReorder}
                startIcon={<ArrowDownwardIcon />}>
                Down
            </Button>
            <Button variant='secondary' size='small' onClick={onAction} startIcon={actionIcon}>
                {actionLabel}
            </Button>
        </Stack>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? 1.5 : 2,
                p: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${outlineColor ?? colors.neutral[200]}`,
                backgroundColor: colors.neutral[0],
            }}>
            {previewBox}
            {meta}
            {actions}
        </Box>
    );
};

const BannerCardSkeleton: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 1.5 : 2,
            p: 1.5,
            borderRadius: 1.5,
            border: `1px solid ${colors.neutral[200]}`,
            backgroundColor: colors.neutral[0],
        }}>
        <Skeleton
            variant='rounded'
            width={isMobile ? '100%' : 168}
            height={isMobile ? 140 : PREVIEW_HEIGHT}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant='text' width='40%' height={20} />
            <Skeleton variant='text' width='60%' height={16} />
        </Box>
    </Box>
);
